import { useCallback, useEffect, useState } from 'react';
import { localizationService } from '@shared-services/localization-service';
import { EcomLocalizationService } from 'client/widget-components/ecom/types/EcomSDK';

type Params = Record<string, string | number>;

const NAMESPACE = 'nativeEcommRuntime.';
const PLURAL_SEPARATOR = '||||';

/**
 * Polyglot's plural forms, which host-services `str()` knows nothing about — it returns the whole
 * value, separator and all, so `shipping_rates.delivery_time` reads
 * "Estimated to be delivered in 2 day. |||| Estimated to be delivered in 2 days." to a shopper.
 *
 * Two forms picked on `smart_count`, because every plural value in this scope carries exactly two.
 * A locale needing more has to resolve through the SDK's polyglot, which applies real plural rules;
 * this is the degraded path that runs only when the SDK service never landed.
 */
function pickPluralForm(value: string, params?: Params): string {
    if (!value.includes(PLURAL_SEPARATOR)) return value;

    const forms = value.split(PLURAL_SEPARATOR).map((form) => form.trim());
    const plural = forms[forms.length - 1] ?? value;
    return Number(params?.smart_count) === 1 ? (forms[0] ?? plural) : plural;
}

/**
 * Placeholders are filled here, not by `str()`: host-services builds a RegExp from each one, and a
 * positional `{0}` is not a valid pattern, so it throws. A replacer function keeps the `$` of a
 * price from being read as a replacement pattern.
 */
function fallbackStr(key: string, params?: Params): string {
    const value = Object.entries(params ?? {}).reduce(
        (text, [name, param]) =>
            text.replaceAll(`{${name}}`, () => String(param)),
        localizationService.str(key)
    );
    return pickPluralForm(value, params);
}

let cachedL10n: EcomLocalizationService | null = null;
let isInitialized = false;
let initPromise: Promise<void> | null = null;
const subscribers = new Set<() => void>();

function readL10nFromWindow(): EcomLocalizationService | null {
    if (typeof window === 'undefined') return null;
    return window._ecomm?.l10n ?? null;
}

function notify() {
    subscribers.forEach((fn) => fn());
}

function ensureInit(): Promise<void> {
    if (initPromise) return initPromise;
    if (isInitialized) return Promise.resolve();
    // SSR or non-Duda host: nothing to wait for; consider ready so consumers
    // don't hang on a promise that will never resolve.
    if (typeof window === 'undefined' || !window._ecomm) {
        isInitialized = true;
        notify();
        return Promise.resolve();
    }
    initPromise = window._ecomm.ready.then(() => {
        cachedL10n = readL10nFromWindow();
        isInitialized = true;
        // Notify regardless of whether the service arrived — readiness consumers
        // need the signal either way; a missing service just falls back to
        // localizationService.
        notify();
    });
    return initPromise;
}

// Kick off resolution on module import so the service lands as early as possible.
if (typeof window !== 'undefined') {
    void ensureInit();
}

export type EcommStr = (key: string, params?: Params) => string;

export function ecommStr(
    l10n: EcomLocalizationService | null,
    key: string,
    params?: Params
): string {
    if (key.startsWith(NAMESPACE)) {
        const l10nKey = key.slice(NAMESPACE.length);
        if (l10n?.exists(l10nKey)) {
            return l10n.localize(l10nKey, params);
        }
    }
    return fallbackStr(key, params);
}

/**
 * The raw SDK localization service, or `null` when it hasn't landed (or the
 * host isn't Duda). Prefer `useEcommStr`; reach for this only when you need
 * `exists()` to branch on whether a key is defined.
 */
export function useEcommL10n(): EcomLocalizationService | null {
    const [l10n, setL10n] = useState<EcomLocalizationService | null>(cachedL10n);

    useEffect(() => {
        if (cachedL10n) {
            if (l10n !== cachedL10n) setL10n(cachedL10n);
            return;
        }
        const onUpdate = () => setL10n(cachedL10n);
        subscribers.add(onUpdate);
        void ensureInit();
        return () => {
            subscribers.delete(onUpdate);
        };
    }, [l10n]);

    return l10n;
}

export function useEcommStr(): EcommStr {
    const l10n = useEcommL10n();

    return useCallback(
        (key: string, params?: Params) => ecommStr(l10n, key, params),
        [l10n]
    );
}

/**
 * Returns `true` once the localization init has settled:
 * - `window._ecomm.ready` resolved (regardless of whether the service was found), or
 * - There is no `_ecomm` host at all (SSR or non-Duda environment).
 *
 * Use this to gate rendering when you want to wait for the proper strings to
 * land before showing labels — otherwise the first paint may use the
 * localizationService fallback (often the literal key).
 */
export function useEcommStrReady(): boolean {
    const [ready, setReady] = useState<boolean>(isInitialized);

    useEffect(() => {
        if (isInitialized) {
            if (!ready) setReady(true);
            return;
        }
        const onUpdate = () => {
            if (isInitialized) setReady(true);
        };
        subscribers.add(onUpdate);
        void ensureInit();
        return () => {
            subscribers.delete(onUpdate);
        };
    }, [ready]);

    return ready;
}
